import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, ClipboardList, LogOut, KeyRound,
  Bell, CalendarCheck, History, GraduationCap, Menu, X, Megaphone,
  Clock, CalendarOff, UserCheck, Settings2, CalendarDays, FileText, Flag,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fmtIST } from "@/lib/utils";

const NAV = {
  admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "nav-admin-dashboard" },
    { to: "/admin/structure", label: "Structure", icon: Building2, testid: "nav-admin-structure" },
    { to: "/admin/people", label: "People", icon: Users, testid: "nav-admin-people" },
    { to: "/admin/records", label: "Attendance Records", icon: ClipboardList, testid: "nav-admin-records" },
    { to: "/admin/staff-attendance", label: "Staff Attendance", icon: UserCheck, testid: "nav-admin-staff-attendance" },
    { to: "/admin/staff-settings", label: "Staff Settings", icon: Settings2, testid: "nav-admin-staff-settings" },
    { to: "/admin/leave", label: "Leave", icon: CalendarOff, testid: "nav-admin-leave" },
    { to: "/admin/appeals", label: "Appeals", icon: Flag, testid: "nav-admin-appeals" },
    { to: "/daily-reports", label: "Daily Reports", icon: FileText, testid: "nav-daily-reports" },
    { to: "/events", label: "Events & Deadlines", icon: CalendarDays, testid: "nav-events" },
    { to: "/notices", label: "Notices", icon: Megaphone, testid: "nav-notices" },
  ],
  teacher: [
    { to: "/teacher", label: "Mark Attendance", icon: CalendarCheck, testid: "nav-teacher-mark" },
    { to: "/teacher/history", label: "My Submissions", icon: History, testid: "nav-teacher-history" },
    { to: "/teacher/clock", label: "Clock In / Out", icon: Clock, testid: "nav-teacher-clock" },
    { to: "/teacher/leave", label: "Leave", icon: CalendarOff, testid: "nav-teacher-leave" },
    { to: "/teacher/appeals", label: "Appeals", icon: Flag, testid: "nav-teacher-appeals" },
    { to: "/daily-reports", label: "Daily Reports", icon: FileText, testid: "nav-daily-reports" },
    { to: "/events", label: "Events & Deadlines", icon: CalendarDays, testid: "nav-events" },
    { to: "/notices", label: "Notices", icon: Megaphone, testid: "nav-notices" },
  ],
  hod_extra: [
    { to: "/admin/records", label: "Student Records", icon: ClipboardList, testid: "nav-hod-records" },
    { to: "/admin/staff-attendance", label: "Staff Attendance", icon: UserCheck, testid: "nav-hod-staff-attendance" },
    { to: "/admin/leave", label: "Leave Records", icon: CalendarOff, testid: "nav-hod-leave" },
  ],
  student: [
    { to: "/student", label: "My Attendance", icon: GraduationCap, testid: "nav-student-dashboard" },
    { to: "/student/appeals", label: "Appeals", icon: Flag, testid: "nav-student-appeals" },
    { to: "/events", label: "Events & Deadlines", icon: CalendarDays, testid: "nav-events" },
    { to: "/notices", label: "Notices", icon: Megaphone, testid: "nav-notices" },
  ],
  parent: [
    { to: "/parent", label: "Child's Attendance", icon: GraduationCap, testid: "nav-parent-dashboard" },
    { to: "/events", label: "Events & Deadlines", icon: CalendarDays, testid: "nav-events" },
    { to: "/notices", label: "Notices", icon: Megaphone, testid: "nav-notices" },
  ],
};

function ChangePasswordDialog() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/change-password", form);
      toast.success("Password changed successfully");
      setOpen(false);
      setForm({ current_password: "", new_password: "" });
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="change-password-open-btn"
          className="text-slate-300 hover:text-white hover:bg-slate-800">
          <KeyRound className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white rounded-md">
        <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cp-current">Current password</Label>
            <Input id="cp-current" data-testid="change-password-current" type="password" required
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-new">New password (min 8 characters)</Label>
            <Input id="cp-new" data-testid="change-password-new" type="password" required minLength={8}
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })} />
          </div>
          <Button type="submit" data-testid="change-password-submit" disabled={loading}
            className="w-full bg-slate-950 hover:bg-slate-800 text-white">
            {loading ? "Saving…" : "Change password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NotificationBell() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const notifPath = user?.role === "teacher" ? "/teacher/notifications" : "/student/notifications";
  const { data: notifications = [] } = useQuery({
    queryKey: ["notifications", user?.role],
    queryFn: async () => (await api.get(notifPath)).data,
    refetchInterval: 30000,
    enabled: user?.role === "student" || user?.role === "teacher",
  });
  const unread = notifications.filter((n) => !n.read).length;

  const markAll = async () => {
    await api.post(`${notifPath}/read-all`);
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="notifications-bell-btn"
          className="relative text-slate-300 hover:text-white hover:bg-slate-800">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span data-testid="notifications-unread-count"
              className="absolute -top-1 -right-1 h-4 min-w-4 px-0.5 rounded-full bg-red-600 text-white text-[10px] font-data flex items-center justify-center">
              {unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 bg-white rounded-md" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <span className="font-heading font-semibold text-sm">Notifications</span>
          {unread > 0 && (
            <button onClick={markAll} data-testid="notifications-mark-all-read"
              className="text-xs text-[#002FA7] hover:underline">Mark all read</button>
          )}
        </div>
        <div className="max-h-80 overflow-auto">
          {notifications.length === 0 && (
            <p className="p-6 text-sm text-slate-500 text-center" data-testid="notifications-empty">No notifications yet.</p>
          )}
          {notifications.map((n) => (
            <div key={n.id} data-testid={`notification-item-${n.id}`}
              className={`px-4 py-3 border-b border-slate-100 ${n.read ? "opacity-60" : "bg-amber-50/60"}`}>
              <p className="text-sm font-medium text-slate-950">{n.title}</p>
              <p className="text-sm text-slate-600 mt-0.5">{n.message}</p>
              <p className="text-xs text-slate-400 mt-1 font-data">{fmtIST(n.created_at)}</p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const base = NAV[user?.role] || [];
  const hodItems = user?.role === "teacher" && user?.is_hod ? NAV.hod_extra : [];
  const items = [...base, ...hodItems];
  const [menuOpen, setMenuOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 lg:flex" data-testid="app-layout">
      {menuOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
          data-testid="mobile-menu-overlay" onClick={() => setMenuOpen(false)} />
      )}
      <aside className={`w-60 shrink-0 bg-slate-950 text-white flex flex-col fixed inset-y-0 z-40 transform transition-transform duration-200 lg:translate-x-0 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 bg-white text-slate-950 flex items-center justify-center rounded-sm">
                <GraduationCap className="h-5 w-5" strokeWidth={2} />
              </div>
              <span className="font-heading font-bold text-lg tracking-tight">Law-Edu-Management</span>
            </div>
            <button className="lg:hidden text-slate-400 hover:text-white" data-testid="mobile-menu-close"
              onClick={() => setMenuOpen(false)}>
              <X className="h-5 w-5" />
            </button>
          </div>
          <Badge variant="outline" data-testid="layout-role-badge"
            className="mt-3 text-slate-300 border-slate-700 capitalize text-[11px]">
            {user?.role}{user?.is_hod ? " · HOD" : ""}
          </Badge>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((item) => (
            <NavLink key={item.to} to={item.to} end data-testid={item.testid}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
                  isActive ? "bg-white text-slate-950 font-medium" : "text-slate-400 hover:text-white hover:bg-slate-800"
                }`}>
              <item.icon className="h-4 w-4" strokeWidth={1.75} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-slate-800 space-y-1">
          <div className="flex items-center justify-between px-3 py-1">
            <span className="text-xs text-slate-400 truncate max-w-[130px]" data-testid="layout-user-name">{user?.name}</span>
            <div className="flex items-center">
              {(user?.role === "student" || user?.role === "teacher") && <NotificationBell />}
              <ChangePasswordDialog />
            </div>
          </div>
          <button onClick={doLogout} data-testid="logout-btn"
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-slate-400 hover:text-white hover:bg-slate-800 transition-colors duration-200 w-full">
            <LogOut className="h-4 w-4" strokeWidth={1.75} />
            Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 lg:ml-60 min-w-0">
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
          <button className="lg:hidden text-slate-700 hover:text-slate-950" data-testid="mobile-menu-open"
            onClick={() => setMenuOpen(true)}>
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="font-heading text-xl font-semibold tracking-tight text-slate-950" data-testid="page-title">{title}</h1>
        </header>
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">{children}</div>
      </main>
    </div>
  );
}
